import React, { Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heading, Text, ProgressCircle } from "@react-spectrum/s2";
import { WelcomeView } from '../components/App/WelcomeView';
import { SiteLandingView } from '../components/App/SiteLandingView';
import { Site, hasPermission, Permission } from '../types';
import { ActiveView, isCommandCenterView, useSiteContext } from '../contexts/SiteContext';
import { usePermissionsContext } from '../contexts/PermissionsContext';
import { useChatContext } from '../contexts/ChatContext';
// Lazy-loaded page components
const ChatPage = React.lazy(()=>import('./ChatPage'));
const EditSitePage = React.lazy(()=>import('./EditSitePage'));
const AddContentPage = React.lazy(()=>import('./AddContentPage'));
const StatsPage = React.lazy(()=>import('./StatsPage'));
const AnalyticsPage = React.lazy(()=>import('./AnalyticsPage'));
const ManageUsersPage = React.lazy(()=>import('./ManageUsersPage'));
const ToolsPage = React.lazy(()=>import('./ToolsPage'));
const HistoryPage = React.lazy(()=>import('./HistoryPage'));
const SettingsPage = React.lazy(()=>import('./SettingsPage'));
const PublishingPage = React.lazy(()=>import('./PublishingPage'));
const SecurityPage = React.lazy(()=>import('./SecurityPage'));
const SampleContentPage = React.lazy(()=>import('./SampleContentPage'));
const DocsPage = React.lazy(()=>import('./DocsPage'));
const CreateSitePage = React.lazy(()=>import('./CreateSitePage'));
const CommandCenterPage = React.lazy(()=>import('./CommandCenterPage'));
const DefaultOrgConfigPage = React.lazy(()=>import('./DefaultOrgConfigPage'));
const ModelsPage = React.lazy(()=>import('./ModelsPage'));
const ModelsLandingPage = React.lazy(()=>import('./ModelsLandingPage'));
const OverviewPage = React.lazy(()=>import('./OverviewPage'));
// Command Center views (isCommandCenterView) never need a selectedSite or an
// org membership check — they're the org-independent admin console.
const COMMAND_CENTER_VIEWS: Partial<Record<ActiveView & string, () => React.ReactNode>> = {
    commandCenter: ()=><CommandCenterPage/>,
    defaultOrgConfig: ()=><DefaultOrgConfigPage/>,
    models: ()=><ModelsPage/>
};
// Views that need an org (userOrgIds.length > 0) but not a selectedSite.
const STANDALONE_VIEWS: Partial<Record<ActiveView & string, () => React.ReactNode>> = {
    security: ()=><SecurityPage/>,
    sampleContent: ()=><SampleContentPage/>,
    documentation: ()=><DocsPage/>,
    orgModels: ()=><ModelsLandingPage/>,
    create: ()=><CreateSitePage/>
};
// Views that require a selectedSite and take no extra props.
const SITE_DEPENDENT_VIEWS: Partial<Record<ActiveView & string, () => React.ReactNode>> = {
    chat: ()=><ChatPage/>,
    history: ()=><HistoryPage/>,
    edit: ()=><EditSitePage/>,
    stats: ()=><StatsPage/>,
    analytics: ()=><AnalyticsPage/>,
    manageUsers: ()=><ManageUsersPage/>,
    tools: ()=><ToolsPage/>
};
/**
 * Dispatches the views that require a selectedSite — split out of
 * renderContent so its own cognitive complexity is scored separately
 * (each nested `selectedSite && activeView === 'x'` check previously added
 * up in a single giant function).
 */ export function renderSiteDependentView(activeView: ActiveView, location: Location, selectedSite: Site, currentUserId?: string): React.ReactNode {
    if (activeView === 'addContent') {
        return <AddContentPage currentUserId={currentUserId}/>;
    }
    const view = activeView && SITE_DEPENDENT_VIEWS[activeView];
    if (view) return view();
    if (/\/mcp-server\/?$/.test(location.pathname)) {
        return <Navigate to={`/domains/${selectedSite.site_id}/settings?settingsTab=mcp`} replace/>;
    }
    if (activeView === 'settings') return <SettingsPage/>;
    if (activeView === 'publishing') {
        // Access is gated by PermissionsContext (org publishing flag + site-scoped
        // MANAGE_CONTENT), which correctly waits for permissions to load before redirecting.
        return <PublishingPage/>;
    }
    return null;
}
interface MainContentProps {
    isAuthenticated: boolean;
    authError: string | null;
    onLogin: () => Promise<void>;
    currentUserId?: string;
}
const MainContent: React.FC<MainContentProps> = ({ isAuthenticated, authError, onLogin, currentUserId })=>{
    const { sites, selectedSite, activeView, userOrgIds, handleOpenCreate, handleAddContent, handleAnalytics, handleHistory } = useSiteContext();
    const { userPermissions } = usePermissionsContext();
    const { handleStartChat } = useChatContext();
    const location = useLocation();
    const { t } = useTranslation();
    const renderContent = ()=>{
        if (!isAuthenticated) {
            return <WelcomeView error={authError} onLogin={onLogin}/>;
        }
        // Command Center routes never require an org membership check below.
        if (isCommandCenterView(activeView)) {
            return COMMAND_CENTER_VIEWS[activeView]?.();
        }
        if (userOrgIds.length === 0) {
            return (<div className={" Tk171 Qk171 Sk171 Rk171"}>
          <Text>
            {t('app.noOrganizations')}
          </Text>
        </div>);
        }
        if (selectedSite) {
            const siteView = renderSiteDependentView(activeView, location, selectedSite, currentUserId);
            if (siteView) return siteView;
        }
        const standaloneView = activeView && STANDALONE_VIEWS[activeView];
        if (standaloneView) return standaloneView();
        if (selectedSite && selectedSite.status === 'deleting') {
            return (<div className={"app-centered-state" + " _ub171 _vb171 _sb171"}>
          <div className="app-centered-state-inner">
            <ProgressCircle aria-label={t('app.deletingContext')} isIndeterminate size="L"/>
            <Heading level={3} styles={" JC171 Gv171"}>
              {t('app.deletingSite', {
                siteName: selectedSite.site_name
            })}
            </Heading>
            <Text>
              {t('app.deletingSiteBody')}
            </Text>
          </div>
        </div>);
        }
        if (selectedSite) {
            return (<SiteLandingView siteName={selectedSite.site_name} onStartChat={handleStartChat} onChatHistory={handleHistory} onAddContent={handleAddContent} onAnalytics={handleAnalytics} canManageContent={hasPermission(userPermissions, Permission.MANAGE_CONTENT)} canManageSite={hasPermission(userPermissions, Permission.UPDATE_SITE)}/>);
        }
        // Default: no site selected, show overview dashboard
        return <OverviewPage/>;
    };
    return (<Suspense fallback={<div className={"app-centered-state" + " Tk171 Qk171 Sk171 Rk171"}>
          <ProgressCircle aria-label={t('app.loading')} isIndeterminate/>
        </div>}>
      {renderContent()}
    </Suspense>);
};
export default MainContent;

import "macro-e67ea437b21703bf49bd8ed98ce85f2a5f6fef700f2e266811ec77a2f06c61d3.css";
import "macro-bab5ef82f09fb5d0f4436f805cbbf1506e94fd514257b2e4f43932c802b98f56.css";
import "macro-2f5a054681d725e6b924239bdb60265a4596dda852904554f6296b5936f4b06b.css";
import "macro-e67ea437b21703bf49bd8ed98ce85f2a5f6fef700f2e266811ec77a2f06c61d3.css";